import type {IntlShape} from '@pinecast/i18n';
import {useIntl} from '@pinecast/i18n';
import type {CSS} from '@pinecast/styles';
import styled, {focusVisibleRing} from '@pinecast/styles';
import * as React from 'react';

const DeleteButton_ = styled(
  'button',
  (
    {
      $idleColor = '#939899',
      disabled,
    }: {
      $idleColor?: string;
      disabled: boolean;
    },
    designColors,
  ) => ({
    backgroundColor: 'transparent',
    border: 0,
    borderRadius: 0,
    cursor: 'pointer',
    display: 'inline-flex',
    flex: '0 0 20px',
    height: 20,
    marginTop: -3,
    opacity: disabled ? 0.5 : 1,
    padding: 0,
    pointerEvents: disabled ? 'none' : 'initial',
    position: 'relative',
    transition: 'opacity 0.2s',
    verticalAlign: 'middle',
    width: 20,

    ':before': {
      backgroundColor: $idleColor,
      bottom: '25%',
      content: '""',
      display: 'block',
      height: 2,
      left: '25%',
      margin: 'auto',
      position: 'absolute',
      right: '25%',
      top: '25%',
      transform: 'rotate(45deg)',
      transformOrigin: 'center',
      transition: 'background-color 0.25s',
    },
    ':after': {
      backgroundColor: $idleColor,
      bottom: '25%',
      content: '""',
      display: 'block',
      height: 2,
      left: '25%',
      margin: 'auto',
      position: 'absolute',
      right: '25%',
      top: '25%',
      transform: 'rotate(-45deg)',
      transformOrigin: 'center',
      transition: 'background-color 0.25s',
    },
    ':hover:before': {
      backgroundColor: '#bf1d1d',
    },
    ':hover:after': {
      backgroundColor: '#bf1d1d',
    },

    // Outside the button. In a Tag it also crosses the tag's fill, and on a
    // category that is not valid it is on a red fill: the contrast test has
    // those pairs.
    ...focusVisibleRing(designColors),
  }),
  {type: 'button'},
);

const DeleteButton = ({
  disabled,
  idleColor,
  label,
  onClick,
  style,
}: {
  disabled?: boolean;
  idleColor?: string;
  label?: (intl: IntlShape) => string;
  onClick: () => void;
  style?: CSS;
}) => {
  const intl = useIntl();
  const labelText =
    label?.(intl) ??
    intl.formatMessage({
      description: 'Delete button label',
      defaultMessage: 'Delete',
    });
  return (
    <DeleteButton_
      $idleColor={idleColor}
      aria-label={labelText}
      disabled={!!disabled}
      onClick={(e: React.SyntheticEvent<HTMLButtonElement>) => {
        e.preventDefault();
        onClick();
      }}
      style={style}
      title={labelText}
    />
  );
};

export default DeleteButton;
